import { ImageAnalysisSample, MLS, MLSID, schAddress } from '@aloft/toolkit-shared';
import {
  Block,
  Button,
  Checkbox,
  Flex,
  SimpleInput,
  SimpleSelect,
  Typography,
} from '@aloft/toolkit-sui';
import { Textarea } from '@aloft/toolkit-sui/chakra';

import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ReactMarkdown from 'react-markdown';

import { AddressInput } from '../../../../components/AddressInput/AddressInput.tsx';
import {
  AnalysisMode,
  CommonFields,
  DEFAULT_MODEL,
  DEFAULT_PROMPTS,
  GEMINI_MODELS,
  ImageAnalysisFormSubmit,
} from './imageAnalysisFormConfig.ts';

export type { AnalysisMode, ImageAnalysisFormSubmit };

type LookupType = 'mlsId' | 'address';

const SORTED_ACTIVE_MLS_KEYS = (MLS._keys.filter((key) => MLS[key].isActive) as MLSID[]).sort(
  (a, b) => (MLS[a].shortName ?? a).localeCompare(MLS[b].shortName ?? b),
);

type Props = {
  onSubmit: (values: ImageAnalysisFormSubmit) => void;
  isSubmitting: boolean;
  analysisMode: AnalysisMode;
};

export const ImageAnalysisForm = ({ onSubmit, isSubmitting, analysisMode }: Props) => {
  const [lookupType, setLookupType] = useState<LookupType>('address');
  const [mlsid, setMlsid] = useState<MLSID>(SORTED_ACTIVE_MLS_KEYS[0]!);
  const [listingID, setListingID] = useState('');
  const [model, setModel] = useState<string>(DEFAULT_MODEL);
  const [promptOverride, setPromptOverride] = useState(DEFAULT_PROMPTS['uadRatings']);
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [sampleType, setSampleType] = useState<'all' | 'first' | 'random'>('all');
  const [sampleCount, setSampleCount] = useState('10');
  const [compress, setCompress] = useState(true);

  const addressForm = useForm({
    defaultValues: {
      address: {
        AddressLineText: '',
        unitNumber: '',
        CityName: '',
        StateCode: '',
        PostalCode: '',
      },
    },
  });

  const watchedAddress = addressForm.watch('address');

  useEffect(() => {
    setPromptOverride(DEFAULT_PROMPTS[analysisMode]);
    setOverrideEnabled(false);
  }, [analysisMode]);

  const handleSubmit = () => {
    const count = Math.max(1, parseInt(sampleCount, 10) || 10);
    let sample: ImageAnalysisSample;
    if (sampleType === 'random') {
      sample = { type: 'random', count };
    } else if (sampleType === 'first') {
      sample = { type: 'first', count };
    } else {
      sample = { type: 'all' };
    }

    const common: CommonFields = {
      analysisMode,
      model,
      promptOverride: overrideEnabled ? promptOverride : undefined,
      sample,
      compress,
      submittedLabel: '',
    };

    if (lookupType === 'mlsId') {
      onSubmit({
        ...common,
        lookupType: 'mlsId',
        mlsid,
        listingID: listingID.trim(),
        submittedLabel: `${MLS[mlsid].shortName ?? mlsid} #${listingID.trim()}`,
      });
    } else {
      const address = schAddress.parse(watchedAddress ?? {});
      const { AddressLineText, CityName, StateCode } = watchedAddress ?? {};
      onSubmit({
        ...common,
        lookupType: 'address',
        address,
        submittedLabel: [AddressLineText, CityName, StateCode].filter(Boolean).join(', '),
      });
    }
  };

  const isSubmitDisabled =
    isSubmitting ||
    (lookupType === 'mlsId' && listingID.trim() === '') ||
    (lookupType === 'address' && !watchedAddress?.AddressLineText);

  return (
    <Flex gap="24px" flexDirection="column">
      {/* Lookup type + model */}
      <Flex gap="16px" flexWrap="wrap" alignItems="flex-end">
        <SimpleSelect
          label="Lookup by"
          value={lookupType}
          onChange={(e) => setLookupType(e.target.value as LookupType)}
          width="160px"
          size="sm"
        >
          <option value="mlsId">MLS ID</option>
          <option value="address">Address</option>
        </SimpleSelect>

        <SimpleSelect
          label="Model"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          width="300px"
          size="sm"
        >
          {GEMINI_MODELS.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </SimpleSelect>
      </Flex>

      {/* MLS ID inputs */}
      {lookupType === 'mlsId' && (
        <Flex gap="16px" flexWrap="wrap" alignItems="flex-end">
          <SimpleSelect
            label="MLS"
            value={mlsid}
            onChange={(e) => setMlsid(e.target.value as MLSID)}
            width="400px"
            size="sm"
          >
            {SORTED_ACTIVE_MLS_KEYS.map((key) => (
              <option key={key} value={key}>
                {MLS[key].shortName ?? key}
              </option>
            ))}
          </SimpleSelect>

          <SimpleInput
            label="Listing ID"
            value={listingID}
            onChange={(e) => setListingID(e.target.value)}
            size="sm"
            width="200px"
            placeholder="e.g. 12345678"
          />
        </Flex>
      )}

      {/* Address input */}
      {lookupType === 'address' && (
        <FormProvider {...addressForm}>
          <AddressInput addressLabel="Property Address" isRequired={false} />
        </FormProvider>
      )}

      {/* Image sample */}
      <Flex gap="16px" flexWrap="wrap" alignItems="flex-end">
        <SimpleSelect
          label="Images"
          value={sampleType}
          onChange={(e) => setSampleType(e.target.value as 'all' | 'first' | 'random')}
          width="160px"
          size="sm"
        >
          <option value="all">All images</option>
          <option value="first">First N</option>
          <option value="random">Random sample</option>
        </SimpleSelect>

        {(sampleType === 'random' || sampleType === 'first') && (
          <SimpleInput
            label="Count"
            value={sampleCount}
            onChange={(e) => setSampleCount(e.target.value)}
            size="sm"
            width="80px"
            placeholder="10"
          />
        )}
        {sampleType === 'random' && (
          <Typography variant="body2" color="textSecondary">
            First 3 photos always included
          </Typography>
        )}
        <Checkbox isChecked={compress} onChange={(e) => setCompress(e.target.checked)}>
          Compress images
        </Checkbox>
      </Flex>

      <Flex>
        <Button text="Run Analysis" onClick={handleSubmit} disabled={isSubmitDisabled} size="sm" />
      </Flex>

      {/* Prompt */}
      <Flex flexDirection="column" gap="8px">
        <Flex alignItems="center" gap="12px">
          <Typography variant="h3">System Prompt</Typography>
          <Checkbox
            isChecked={overrideEnabled}
            onChange={(e) => setOverrideEnabled(e.target.checked)}
          >
            Override
          </Checkbox>
        </Flex>

        {overrideEnabled ? (
          <Textarea
            value={promptOverride}
            onChange={(e) => setPromptOverride(e.target.value)}
            style={{
              width: '100%',
              minHeight: '600px',
              resize: 'vertical',
              fontSize: '12px',
              fontFamily: 'monospace',
              padding: '8px',
            }}
          />
        ) : (
          <Block
            padding="16px"
            borderRadius="4px"
            backgroundColor="surface2"
            overflow="auto"
            maxHeight="600px"
          >
            <ReactMarkdown>{DEFAULT_PROMPTS[analysisMode]}</ReactMarkdown>
          </Block>
        )}
      </Flex>
    </Flex>
  );
};
